import Request from './../../request';
import moment from 'moment';

// import props
import {
  IShipDocumentResourceShortProps,
  IShipScheduleDelayResourceShort,
  IShipScheduleResourceShort,
} from '../../../props/data/ships';
import { IOrderResourceProps } from '../../../props/orders/order';
import { IPaymentResourceShortProps } from '../../../props/payments';
import { IDocumentProps } from '../../../props/general';

const ShipSchedulesService = {
  isExpired: (schedule: IShipScheduleResourceShort): boolean => {
    if (!schedule) return true;

    // Check status
    const finishedStatuses = ['completed', 'arrived', 'finished', 'done', 'cancelled', 'closed'];
    if (schedule.status && finishedStatuses.includes(schedule.status.toLowerCase())) {
      return true;
    }

    // Determine effective departure date
    let departureDateStr = schedule.actualDeparture;
    if (!departureDateStr && schedule.delays && schedule.delays.length > 0) {
      const sortedDelays = [...schedule.delays].sort((a, b) =>
        moment(b.createdAt || b.newEstimatedDeparture).valueOf() - moment(a.createdAt || a.newEstimatedDeparture).valueOf()
      );
      departureDateStr = sortedDelays[0]?.newEstimatedDeparture;
    }
    if (!departureDateStr) {
      departureDateStr = schedule.estimatedDeparture;
    }

    if (departureDateStr) {
      const depMoment = moment(departureDateStr);
      if (depMoment.isValid() && depMoment.isBefore(moment().startOf('day'))) {
        return true;
      }
    }

    // Check actualArrival
    if (schedule.actualArrival) {
      const arrMoment = moment(schedule.actualArrival);
      if (arrMoment.isValid() && arrMoment.isBefore(moment().startOf('day'))) {
        return true;
      }
    }

    return false;
  },
  filterActive: (schedules: IShipScheduleResourceShort[], preserveSelectedId?: string): IShipScheduleResourceShort[] => {
    if (!Array.isArray(schedules)) return [];
    return schedules.filter((item) => {
      if (preserveSelectedId && item.id === preserveSelectedId) {
        return true;
      }
      return !ShipSchedulesService.isExpired(item);
    });
  },
  retrieve: async (qs?: string): Promise<IShipScheduleResourceShort[]> => {
    try {
      const results = await Request.get(`/api/operationals/ships/schedules?${qs || ""}`);
      return results;
    } catch (e) {
      throw e;
    }
  },
  get: async (id: string): Promise<IShipScheduleResourceShort> => {
    try {
      const item = await Request.get(`/api/operationals/ships/schedules/${id}`);
      return item;
    } catch (e) {
      throw e;
    }
  },
  getDelays: async (id: string): Promise<IShipScheduleDelayResourceShort[]> => {
    try {
      const item = await Request.get(`/api/operationals/ships/schedules/${id}/delays`);
      return item;
    } catch (e) {
      throw e;
    }
  },
  new: async (data: FormData): Promise<IShipScheduleResourceShort> => {
    try {
      const result = await Request.post(`/api/operationals/ships/schedules`, data, 'multipart/form-data');
      return result;
    } catch (e) {
      throw e;
    }
  },
  update: async (id: string, data: FormData): Promise<IShipScheduleResourceShort> => {
    try {
      const result = await Request.post(
        `/api/operationals/ships/schedules/${id}?_method=PUT`,
        data,
        'multipart/form-data'
      );
      return result;
    } catch (e) {
      throw e;
    }
  },
  delete: async (id: string): Promise<void> => {
    try {
      await Request.delete(`/api/operationals/ships/schedules/${id}`);
      return;
    } catch (e) {
      throw e;
    }
  },
  getOrders: async (id: string): Promise<IOrderResourceProps[]> => {
    try {
      const item = await Request.get(`/api/data/ships/schedules/${id}/orders`);
      return item;
    } catch (e) {
      throw e;
    }
  },
  getPayments: async (id: string): Promise<IPaymentResourceShortProps[]> => {
    try {
      const item = await Request.get(`/api/operationals/ships/ships/schedules/${id}/payments`);
      return item;
    } catch (e) {
      throw e;
    }
  },
  documents: {
    retrieve: async (id: string, qs?: string): Promise<IDocumentProps[]> => {
      try {
        const results = await Request.get(`/api/shipSchedules/${id}/documents?${qs || ""}`);
        return results;
      } catch (e) {
        throw (e);
      }
    },
    new: async (id: string, data: FormData): Promise<IDocumentProps> => {
      try {
        const result = await Request.post(`/api/shipSchedules/${id}/documents`, data, 'multipart/form-data');
        return result;
      } catch (e) {
        throw (e);
      }
    },
    delete: async (id: string, data: FormData): Promise<IDocumentProps> => {
      try {
        const result = await Request.post(`/api/shipSchedules/${id}/delete_documents`, data);
        return result;
      } catch (e) {
        throw (e);
      }
    }
  },
};


const ShipScheduleDocumentsService = {
  retrieve: async (scheduleId: string, keyword?: string): Promise<IDocumentProps[]> => {
    const queries: string[] = [];
    if (keyword && keyword.trim() !== '') {
      queries.push(`keyword=${keyword}`);
    }
    try {
      const results = await Request.get(`/api/data/shipSchedules/${scheduleId}/documents?${queries || ''}`);
      return results;
    } catch (e) {
      throw e;
    }
  },
  new: async (scheduleId: string, data: FormData): Promise<IDocumentProps> => {
    try {
      const result = await Request.post(
        `/api/data/shipSchedules/${scheduleId}/documents`,
        data,
        'multipart/form-data'
      );
      return result;
    } catch (e) {
      throw e;
    }
  },
  delete: async (scheduleId: string, id: string): Promise<void> => {
    try {
      await Request.delete(`/api/data/shipSchedules/${scheduleId}/documents/${id}`);
      return;
    } catch (e) {
      throw e;
    }
  },
};

const ShipScheduleImagesService = {
  retrieve: async (scheduleId: string, keyword?: string): Promise<IShipDocumentResourceShortProps[]> => {
    const queries: string[] = [];
    if (keyword && keyword.trim() !== '') {
      queries.push(`keyword=${keyword}`);
    }
    try {
      const results = await Request.get(`/api/data/shipSchedules/${scheduleId}/images?${queries || ''}`);
      return results;
    } catch (e) {
      throw e;
    }
  },
  delete: async (scheduleId: string, id: string): Promise<void> => {
    try {
      await Request.delete(`/api/data/shipSchedules/${scheduleId}/images/${id}`);
      return;
    } catch (e) {
      throw e;
    }
  },
};

export { ShipScheduleDocumentsService, ShipScheduleImagesService };

export default ShipSchedulesService;
